import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/pro-light-svg-icons/faBookmark';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalog/catalogArtwork';
import { dayOfIso } from './presaveCreateFacts';
import { formatReleaseDay } from './formatReleaseDay';
import { presaveCreateMessages } from './presaveCreateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveSongPickerProps {
  /** The unreleased songs, in the order to show (`pickableSongs`). */
  songs: CatalogItem[];
  onPick: (item: CatalogItem) => void;
  onAddSong: () => void;
  onSmartLink: () => void;
}

interface SongRowProps {
  item: CatalogItem;
  onPick: (item: CatalogItem) => void;
}

const SongRow: React.FC<SongRowProps> = ({ item, onPick }) => {
  const intl = useIntl();
  const cover = catalogArtworkSrc(item, 48);
  const day = dayOfIso(item.releaseDate);
  const hasPage = item.presaveCampaignUuid !== null;
  const facts = useMemo(
    () =>
      [
        item.releaseType,
        day === undefined ? intl.formatMessage(presaveCreateMessages.pickNoDay) : formatReleaseDay(intl, day),
      ]
        .filter((part) => part !== null && part !== '')
        .join(' · '),
    [day, intl, item.releaseType]
  );
  return (
    <button
      type="button"
      className={clsx(styles.row, hasPage && styles.rowDim)}
      onClick={() => onPick(item)}
      data-testid={`presave-pick-${item.uuid}`}
    >
      {cover === undefined ? (
        <span className={clsx(styles.art, styles.artEmpty, styles.rowArt)} />
      ) : (
        <span className={clsx(styles.art, styles.rowArt)}>
          <img src={cover} alt="" />
        </span>
      )}
      <span>
        <b>{item.title}</b>
        <small>{facts}</small>
      </span>
      {hasPage ? (
        <span className={clsx(styles.pill, styles.pillOut)}>
          {intl.formatMessage(presaveCreateMessages.pickHasPage)}
        </span>
      ) : (
        <span className={styles.rowPick}>{intl.formatMessage(presaveCreateMessages.pickRow)}</span>
      )}
    </button>
  );
};

/**
 * A pre-save with no song yet (design presave-create c1, c2): the unreleased songs of the artist,
 * then "Add a new song". A pick opens the song's pre-save screen. Released songs get a SmartLink.
 */
const PresaveSongPickerComponent: React.FC<PresaveSongPickerProps> = ({ songs, onPick, onAddSong, onSmartLink }) => {
  const intl = useIntl();
  const isEmpty = songs.length === 0;

  return (
    <div className={clsx(styles.body, isEmpty && styles.bodySingle)} data-testid="presave-picker">
      <div className={styles.qa}>
        <div>
          <div className={styles.eyebrow}>{intl.formatMessage(presaveCreateMessages.eyebrow)}</div>
          <h1 className={styles.question}>
            {intl.formatMessage(isEmpty ? presaveCreateMessages.emptyQuestion : presaveCreateMessages.pickQuestion)}
          </h1>
          <p className={styles.sub}>
            {intl.formatMessage(isEmpty ? presaveCreateMessages.emptySub : presaveCreateMessages.pickSub)}
          </p>
        </div>
        {isEmpty ? (
          <div className={clsx(styles.next, styles.nextPrimary, styles.emptyCard)}>
            <span className={clsx(styles.kbox, styles.kboxBrand)}>
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <b>{intl.formatMessage(presaveCreateMessages.pickAddButton)}</b>
            <small>{intl.formatMessage(presaveCreateMessages.emptyCardBody)}</small>
            <SMLButton size="sm" color="primary" omitCap onClick={onAddSong} testID="presave-add-song">
              {intl.formatMessage(presaveCreateMessages.pickAddButton)}
            </SMLButton>
          </div>
        ) : (
          <>
            <div className={styles.list}>
              <div className={styles.groupLabel}>{intl.formatMessage(presaveCreateMessages.pickGroup)}</div>
              {songs.map((item) => (
                <SongRow key={item.uuid} item={item} onPick={onPick} />
              ))}
            </div>
            <div className={styles.shortcut}>
              <span className={clsx(styles.kbox, styles.kboxBrand)}>
                <FontAwesomeIcon icon={faPlus} />
              </span>
              <div className={styles.grow}>
                <b>{intl.formatMessage(presaveCreateMessages.pickAddTitle)}</b>
                <small>{intl.formatMessage(presaveCreateMessages.pickAddBody)}</small>
              </div>
              <SMLButton outline omitCap onClick={onAddSong} testID="presave-add-song">
                {intl.formatMessage(presaveCreateMessages.pickAddButton)}
              </SMLButton>
            </div>
          </>
        )}
        <span className={styles.hint}>
          {intl.formatMessage(presaveCreateMessages.pickOut)}{' '}
          <button type="button" className={styles.link} onClick={onSmartLink}>
            {intl.formatMessage(presaveCreateMessages.pickOutLink)}
          </button>
        </span>
      </div>
      {!isEmpty && (
        <aside className={styles.card}>
          <div className={styles.cardHead}>
            <span>{intl.formatMessage(presaveCreateMessages.fansGetHead)}</span>
          </div>
          <span className={styles.sideLine}>
            <FontAwesomeIcon icon={faBookmark} className={styles.sideIcon} />
            {intl.formatMessage(presaveCreateMessages.fansGetSave)}
          </span>
          <span className={styles.sideLine}>
            <FontAwesomeIcon icon={faClock} className={styles.sideIcon} />
            {intl.formatMessage(presaveCreateMessages.fansGetCountdown)}
          </span>
          <span className={styles.sideLine}>
            <FontAwesomeIcon icon={faMusic} className={styles.sideIcon} />
            {intl.formatMessage(presaveCreateMessages.fansGetLibrary)}
          </span>
        </aside>
      )}
    </div>
  );
};

export const PresaveSongPicker = TypedMemo(PresaveSongPickerComponent);
